// frontend/app/components/lessonPlans/teachsense/studentIdentity.js
//
// How a student is named on a teacher screen.
//
// This lived in LessonInsightsPanel.js until TeachSense arrived. Every one of
// its sections names students too, and importing the helpers back out of the
// panel they are composed into would be an import cycle — so they moved down
// here and the panel re-exports them, which keeps everything it has always
// exported exported.
//
// No student's email is ever painted. These screens go on a projector, so the
// address lives only in accessibilityLabel and the visible name comes from the
// payload — or, failing that, from a humanized local part shown a step down in
// contrast, because a guess should not read like a fact.

/**
 * The name to paint. `student_name` is a real name or null; the email is never
 * a fallback for display, only the raw material a readable guess is made from.
 */
export const displayName = (entry) => {
  const given = String(entry?.student_name || "").trim();
  if (given) return given;
  const local = String(entry?.student_email || "").split("@")[0];
  if (!local) return "Unknown student";
  return local
    .replace(/[._+-]+/g, " ")
    .replace(/(\d+)/g, " $1")
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

export const initialsOf = (name) => {
  // The first LETTER of each part, not the first character: a part can begin
  // with punctuation — a bracketed tag, a quoted nickname — and taking [0]
  // blindly puts that punctuation on the avatar.
  const letters = String(name)
    .split(/\s+/)
    .map((part) => (part.match(/[A-Za-z]/) || [])[0])
    .filter(Boolean);
  if (!letters.length) return "?";
  return (letters[0] + (letters.length > 1 ? letters[letters.length - 1] : "")).toUpperCase();
};

/** A name humanized out of an email is a guess, so callers draw it in lower contrast. */
export const isInferredName = (entry) => !String(entry?.student_name || "").trim();

/**
 * No name and no address: an absence of identity rather than an identity, so it
 * gets a question mark rather than the initials of the placeholder wording.
 */
export const isAnonymous = (entry) =>
  isInferredName(entry) && !String(entry?.student_email || "").trim();
